import type {
  Book,
  BookLibrary,
  ReadingStatus,
  StarRating,
} from "../models/types";
import { createEmptyLibrary, generateId } from "../models/library";

/**
 * Import books from a Goodreads CSV export.
 *
 * To export from Goodreads:
 *   1. Go to https://www.goodreads.com/review/import
 *   2. Click "Export Library"
 *   3. Download the CSV file
 *   4. Use this importer to convert it
 */

interface GoodreadsRow {
  Title: string;
  Author: string;
  "Author l-f": string;
  ISBN: string;
  ISBN13: string;
  "My Rating": string;
  "Average Rating": string;
  Publisher: string;
  Binding: string;
  "Number of Pages": string;
  "Year Published": string;
  "Original Publication Year": string;
  "Date Read": string;
  "Date Added": string;
  Bookshelves: string;
  "Exclusive Shelf": string;
  "My Review": string;
  [key: string]: string;
}

/** Parse a CSV string into rows */
function parseCSV(csv: string): GoodreadsRow[] {
  const lines = csv.split("\n");
  if (lines.length < 2) return [];

  const headers = parseCSVLine(lines[0]);
  const rows: GoodreadsRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = parseCSVLine(line);
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => {
      row[h] = values[idx] || "";
    });
    rows.push(row as GoodreadsRow);
  }

  return rows;
}

/** Parse a single CSV line respecting quoted fields */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++; // skip next quote
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
  }
  result.push(current.trim());
  return result;
}

function mapStatus(exclusiveShelf: string): ReadingStatus {
  switch (exclusiveShelf.toLowerCase().trim()) {
    case "currently-reading":
      return "reading";
    case "read":
      return "read";
    case "to-read":
    default:
      return "want-to-read";
  }
}

function mapRating(rating: string): StarRating | undefined {
  const num = parseInt(rating, 10);
  if (num >= 1 && num <= 5) return num as StarRating;
  return undefined;
}

function cleanISBN(isbn: string): string {
  // Goodreads exports ISBN as ="0123456789"
  return isbn.replace(/[="]/g, "").trim();
}

/** Import from Goodreads CSV content and return a BookLibrary */
export function importFromGoodreads(csvContent: string): BookLibrary {
  const rows = parseCSV(csvContent);
  const library = createEmptyLibrary();

  for (const row of rows) {
    const status = mapStatus(row["Exclusive Shelf"]);
    const rating = status === "read" ? mapRating(row["My Rating"]) : undefined;
    const isbn13 = cleanISBN(row["ISBN13"]);
    const isbn10 = cleanISBN(row["ISBN"]);

    const book: Book = {
      id: generateId(),
      title: row["Title"] || "Unknown Title",
      author: row["Author"] || row["Author l-f"] || "Unknown Author",
      datePublished:
        row["Original Publication Year"] || row["Year Published"] || "",
      isbn: isbn13 || isbn10 || undefined,
      status,
      rating,
      recommendations: [],
      dateAdded: row["Date Added"]
        ? new Date(row["Date Added"]).toISOString()
        : new Date().toISOString(),
      dateRead: row["Date Read"]
        ? new Date(row["Date Read"]).toISOString()
        : undefined,
      notes: row["My Review"] || undefined,
    };

    library.books.push(book);
  }

  return library;
}

/** Import Goodreads CSV from a File input and return a library */
export function importGoodreadsFile(): Promise<BookLibrary> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }
      try {
        const text = await file.text();
        const library = importFromGoodreads(text);
        resolve(library);
      } catch (err) {
        reject(err);
      }
    });
    input.click();
  });
}
